/**
 * Criterion selection and feedback track helpers for Medical Necessity analytics.
 * @module medicalNecessityAnalytics/feedbackTrackers
 */
import { NCCNEvaluationRecord } from "../../../data-model/nccnEvaluation";
import { EvaluationItem, HumanVerdict } from "../../../data-model/nccnGuidelines";
import { EventsName } from "../../../enums/eventsName";
import { FeedbackInput } from "../utils/nccnReviewFeedback";
import { buildLocationAttrs, MnLocationAttrs } from "./locationAttrs";
import {
  CriterionOutcome,
  deriveHumanCriterionAnswer,
  mapAiCriterionAnswer,
  mapCriterionOutcome,
  mapHumanGuidelineVerdict,
  mapIncorrectReasonType,
} from "./mappers";
import {
  createAnalyticsId,
  MedicalNecessityAnalyticsSession,
} from "./sessionState";

/** Shared deps for feedback trackers. */
export interface FeedbackTrackerDeps {
  analysisId: string;
  session: MedicalNecessityAnalyticsSession;
  emit: (eventName: EventsName, additional: Record<string, unknown>) => void;
}

/**
 * Builds Correct/Incorrect selection and Incorrect-feedback trackers.
 *
 * @param deps - Analysis ID, session, and emit
 * @returns Feedback track methods
 */
export const createFeedbackTrackers = ({
  analysisId,
  session,
  emit,
}: FeedbackTrackerDeps) => {
  /**
   * Builds location attrs for the current criterion visit.
   *
   * @param guideline - Current guideline
   * @param guidelineIndex - One-based display index
   * @param item - Current criterion
   * @param criterionIndex - One-based criterion index
   * @returns Location attribute bag for criterion events
   */
  const criterionLoc = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    item: EvaluationItem,
    criterionIndex: number,
  ): MnLocationAttrs =>
    buildLocationAttrs(
      analysisId,
      guideline,
      guidelineIndex,
      item,
      criterionIndex,
      session.getCriterionVisit(guideline.guideline_id ?? "", item.id) ||
        undefined,
    );

  const trackCriterionOutcomeSelected = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    item: EvaluationItem,
    criterionIndex: number,
    outcome: CriterionOutcome,
  ) => {
    if (outcome === "correct") {
      session.clearPendingFeedback();
    }
    emit(
      outcome === "correct"
        ? EventsName.MEDICAL_NECESSITY_CRITERION_CORRECT_SELECTED
        : EventsName.MEDICAL_NECESSITY_CRITERION_INCORRECT_SELECTED,
      {
        ...criterionLoc(guideline, guidelineIndex, item, criterionIndex),
      },
    );
  };

  /**
   * Emits `annotation_clicked` when the reviewer starts a text annotation.
   *
   * @param guideline - Current guideline
   * @param guidelineIndex - One-based display index
   * @param item - Current criterion
   * @param criterionIndex - One-based criterion index
   * @returns The action id correlating the ensuing annotation save
   */
  const trackAnnotationClicked = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    item: EvaluationItem,
    criterionIndex: number,
  ): string => {
    const actionId = createAnalyticsId();
    emit(EventsName.MEDICAL_NECESSITY_ANNOTATION_CLICKED, {
      ...criterionLoc(guideline, guidelineIndex, item, criterionIndex),
      action_id: actionId,
    });
    return actionId;
  };

  /**
   * Emits `screenshot_clicked` when the reviewer starts a screenshot capture.
   *
   * @param guideline - Current guideline
   * @param guidelineIndex - One-based display index
   * @param item - Current criterion
   * @param criterionIndex - One-based criterion index
   * @returns The action id correlating the ensuing screenshot save
   */
  const trackScreenshotClicked = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    item: EvaluationItem,
    criterionIndex: number,
  ): string => {
    const actionId = createAnalyticsId();
    emit(EventsName.MEDICAL_NECESSITY_SCREENSHOT_CLICKED, {
      ...criterionLoc(guideline, guidelineIndex, item, criterionIndex),
      action_id: actionId,
    });
    return actionId;
  };

  /**
   * Emits `reference_comment_selected` when the reviewer switches the feedback
   * kind dropdown to Comment.
   *
   * @param guideline - Current guideline
   * @param guidelineIndex - One-based display index
   * @param item - Current criterion
   * @param criterionIndex - One-based criterion index
   * @returns The action id correlating the ensuing comment save
   */
  const trackReferenceCommentSelected = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    item: EvaluationItem,
    criterionIndex: number,
  ): string => {
    const actionId = createAnalyticsId();
    emit(EventsName.MEDICAL_NECESSITY_REFERENCE_COMMENT_SELECTED, {
      ...criterionLoc(guideline, guidelineIndex, item, criterionIndex),
      action_id: actionId,
    });
    return actionId;
  };

  /**
   * Emits `screenshot_retake_clicked` when the reviewer clicks Retake in the
   * Review Screenshot modal.
   *
   * @param guideline - Current guideline
   * @param guidelineIndex - One-based display index
   * @param item - Current criterion
   * @param criterionIndex - One-based criterion index
   * @returns The action id correlating the ensuing screenshot save
   */
  const trackScreenshotRetakeClicked = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    item: EvaluationItem,
    criterionIndex: number,
  ): string => {
    const actionId = createAnalyticsId();
    emit(EventsName.MEDICAL_NECESSITY_SCREENSHOT_RETAKE_CLICKED, {
      ...criterionLoc(guideline, guidelineIndex, item, criterionIndex),
      action_id: actionId,
    });
    return actionId;
  };

  /**
   * Emits `annotation_reselect_clicked` when the reviewer clicks Select Again
   * in the Review Annotation modal.
   *
   * @param guideline - Current guideline
   * @param guidelineIndex - One-based display index
   * @param item - Current criterion
   * @param criterionIndex - One-based criterion index
   * @returns The action id correlating the ensuing annotation save
   */
  const trackAnnotationReselectClicked = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    item: EvaluationItem,
    criterionIndex: number,
  ): string => {
    const actionId = createAnalyticsId();
    emit(EventsName.MEDICAL_NECESSITY_ANNOTATION_RESELECT_CLICKED, {
      ...criterionLoc(guideline, guidelineIndex, item, criterionIndex),
      action_id: actionId,
    });
    return actionId;
  };

  /**
   * Emits `next_clicked` when the reviewer advances from a criterion.
   *
   * @param guideline - Current guideline
   * @param guidelineIndex - One-based display index
   * @param item - Current criterion
   * @param criterionIndex - One-based criterion index
   * @returns The action id correlating the ensuing view event
   */
  const trackNextClicked = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    item: EvaluationItem,
    criterionIndex: number,
  ): string => {
    const actionId = createAnalyticsId();
    emit(EventsName.MEDICAL_NECESSITY_NEXT_CLICKED, {
      ...criterionLoc(guideline, guidelineIndex, item, criterionIndex),
      action_id: actionId,
    });
    return actionId;
  };

  /**
   * Emits `guideline_verdict_selected` on every Met / Not Met selection.
   *
   * @param guideline - Current guideline
   * @param guidelineIndex - One-based display index
   * @param verdict - The human verdict just selected
   */
  const trackGuidelineVerdictSelected = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    verdict: HumanVerdict,
  ) => {
    emit(EventsName.MEDICAL_NECESSITY_GUIDELINE_VERDICT_SELECTED, {
      ...buildLocationAttrs(analysisId, guideline, guidelineIndex),
      verdict_selected: mapHumanGuidelineVerdict(verdict),
    });
  };

  /**
   * Emits `annotation_saved` when a text annotation is confirmed.
   *
   * @param guideline - Current guideline
   * @param guidelineIndex - One-based display index
   * @param item - Current criterion
   * @param criterionIndex - One-based criterion index
   * @param comment - Annotation comment (may be empty)
   * @param actionId - Action id of the originating click, when available
   */
  const trackAnnotationSaved = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    item: EvaluationItem,
    criterionIndex: number,
    comment: string,
    actionId?: string,
  ) => {
    emit(EventsName.MEDICAL_NECESSITY_ANNOTATION_SAVED, {
      ...criterionLoc(guideline, guidelineIndex, item, criterionIndex),
      ...(actionId ? { action_id: actionId } : {}),
      comment_provided: comment.trim().length > 0,
    });
  };

  /**
   * Emits `screenshot_saved` when a screenshot is uploaded.
   *
   * @param guideline - Current guideline
   * @param guidelineIndex - One-based display index
   * @param item - Current criterion
   * @param criterionIndex - One-based criterion index
   * @param comment - Screenshot comment (may be empty)
   * @param actionId - Action id of the originating click, when available
   */
  const trackScreenshotSaved = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    item: EvaluationItem,
    criterionIndex: number,
    comment: string,
    actionId?: string,
  ) => {
    emit(EventsName.MEDICAL_NECESSITY_SCREENSHOT_SAVED, {
      ...criterionLoc(guideline, guidelineIndex, item, criterionIndex),
      ...(actionId ? { action_id: actionId } : {}),
      comment_provided: comment.trim().length > 0,
    });
  };

  /**
   * Records the Incorrect feedback the reviewer is about to persist.
   *
   * The actual `feedback_submitted` event is emitted by
   * {@link trackFeedbackSubmitted} once the draft commits, so a cancelled or
   * superseded save never reports a submission.
   *
   * @param guideline - Current guideline
   * @param guidelineIndex - One-based display index
   * @param item - Current criterion
   * @param criterionIndex - One-based criterion index
   * @param input - Feedback form payload
   */
  const trackSaveFeedbackClicked = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    item: EvaluationItem,
    criterionIndex: number,
    input: FeedbackInput,
  ): void => {
    if (input.type === "correct") return;
    // Removing the last annotation clears the verdict instead of submitting a
    // reason, so any earlier Save Feedback must not persist as a submission.
    if (input.type === "unreviewed") {
      session.clearPendingFeedback();
      return;
    }
    const actionId = createAnalyticsId();
    const feedbackType = mapIncorrectReasonType(input.type);
    session.setPendingFeedback({
      actionId,
      criterionId: item.id,
      feedbackType,
      // Carry the comment text so `comment_saved` can be emitted once the
      // draft commits, rather than optimistically on the Save click.
      ...(input.type === "incorrect_comment" ? { comment: input.comment } : {}),
    });
  };

  /**
   * Emits `feedback_submitted` for the pending Incorrect feedback once the
   * criterion draft has committed.
   *
   * @param guideline - Current guideline
   * @param guidelineIndex - One-based display index
   * @param item - Current criterion
   * @param criterionIndex - One-based criterion index
   */
  const trackFeedbackSubmitted = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    item: EvaluationItem,
    criterionIndex: number,
  ) => {
    const pending = session.consumePendingFeedback(item.id);
    if (!pending) return;
    emit(EventsName.MEDICAL_NECESSITY_FEEDBACK_SUBMITTED, {
      ...criterionLoc(guideline, guidelineIndex, item, criterionIndex),
      action_id: pending.actionId,
      feedback_target: "criterion",
      feedback_type: pending.feedbackType,
    });
    // A comment is only "saved" once the draft commits, so emit `comment_saved`
    // here (not on the Save click) to avoid reporting a save that never happened.
    if (pending.feedbackType === "comment" && pending.comment) {
      trackCommentSaved(
        guideline,
        guidelineIndex,
        item,
        criterionIndex,
        pending.comment,
        pending.actionId,
      );
    }
  };

  /**
   * Emits `comment_saved` when the reviewer saves a free-text comment.
   *
   * @param guideline - Current guideline
   * @param guidelineIndex - One-based display index
   * @param item - Current criterion
   * @param criterionIndex - One-based criterion index
   * @param comment - Saved comment text
   * @param actionId - Action id of the originating click, when available
   */
  const trackCommentSaved = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    item: EvaluationItem,
    criterionIndex: number,
    comment: string,
    actionId?: string,
  ) => {
    emit(EventsName.MEDICAL_NECESSITY_COMMENT_SAVED, {
      ...criterionLoc(guideline, guidelineIndex, item, criterionIndex),
      ...(actionId ? { action_id: actionId } : {}),
      comment_provided: comment.trim().length > 0,
    });
  };

  /**
   * Emits `back_clicked` from a criterion or the decision screen.
   *
   * @param guideline - Current guideline
   * @param guidelineIndex - One-based display index
   * @param item - Current criterion (criterion step only)
   * @param criterionIndex - One-based criterion index (criterion step only)
   * @returns The action id correlating the ensuing view event
   */
  const trackBackClicked = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    item?: EvaluationItem,
    criterionIndex?: number,
  ): string => {
    const actionId = createAnalyticsId();
    const loc =
      item && criterionIndex != null
        ? criterionLoc(guideline, guidelineIndex, item, criterionIndex)
        : buildLocationAttrs(analysisId, guideline, guidelineIndex);
    emit(EventsName.MEDICAL_NECESSITY_BACK_CLICKED, {
      ...loc,
      rhs_stage: session.getRhsStage(),
      action_id: actionId,
    });
    return actionId;
  };

  /**
   * Emits `criterion_completed` once the criterion state persists on Next.
   *
   * @param guideline - Current guideline
   * @param guidelineIndex - One-based display index
   * @param item - Current criterion
   * @param criterionIndex - One-based criterion index
   * @param actionId - Action id of the Next click that completed it
   */
  const trackCriterionCompleted = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    item: EvaluationItem,
    criterionIndex: number,
    actionId?: string,
  ) => {
    const outcome = mapCriterionOutcome(item);
    const references = item.reviewer_references ?? [];
    const attrs: Record<string, unknown> = {
      ...criterionLoc(guideline, guidelineIndex, item, criterionIndex),
      ...(actionId ? { action_id: actionId } : {}),
      criterion_outcome: outcome,
      ai_criterion_answer: mapAiCriterionAnswer(item.verdict),
      human_criterion_answer: deriveHumanCriterionAnswer(item),
    };
    if (outcome === "incorrect") {
      attrs.num_annotations = references.filter(
        (reference) => !reference.image_path,
      ).length;
      attrs.num_screenshots = references.filter(
        (reference) => Boolean(reference.image_path),
      ).length;
      attrs.num_comments = (item.reviewer_comments ?? []).length;
    }
    emit(EventsName.MEDICAL_NECESSITY_CRITERION_COMPLETED, attrs);
  };

  return {
    trackCriterionOutcomeSelected,
    trackAnnotationClicked,
    trackScreenshotClicked,
    trackReferenceCommentSelected,
    trackScreenshotRetakeClicked,
    trackAnnotationReselectClicked,
    trackNextClicked,
    trackGuidelineVerdictSelected,
    trackAnnotationSaved,
    trackScreenshotSaved,
    trackSaveFeedbackClicked,
    trackFeedbackSubmitted,
    trackCommentSaved,
    trackBackClicked,
    trackCriterionCompleted,
  };
};
